import { Button, Form, Input } from 'antd';
import React, { useState } from 'react';
import api from '../../../api';
import { ID } from '../../../global';
import { Page, PageParam } from '@/models/common';
import { Staff, StaffEndpoint } from '@/models/staff';
import { createModalSelector } from '../core/createModalSelector';
import { createModalViewer } from '../core/createModalViewer';
import { createSelector } from '../core/createSelector';
import { DataSource } from '../core/datasource';
import { createFieldViewer } from '../core/createFieldViewer';

type StaffFilterValue = { nickname: string };
export const StaffFilter: React.FC<{ onFilter: (filters: StaffFilterValue) => void }> = ({ onFilter }) => {
  const [form] = Form.useForm();
  return (
    <Form<StaffFilterValue> form={form} layout="inline" onFinish={onFilter}>
      <Form.Item name="nickname" label="员工姓名">
        <Input placeholder="请输入" />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit">
          搜索
        </Button>
      </Form.Item>
    </Form>
  );
};

// 模块级缓存：多个 StaffFieldViewer 实例共享，避免重复请求
const staffViewCache = new Map<number, Staff>();
let pendingStaffIds = new Set<number>();
let pendingBatchPromise: Promise<void> | null = null;

function scheduleStaffBatchFlush() {
  if (pendingBatchPromise) return pendingBatchPromise;

  pendingBatchPromise = new Promise<void>((resolve, reject) => {
    setTimeout(async () => {
      const ids = Array.from(pendingStaffIds);
      pendingStaffIds = new Set();
      pendingBatchPromise = null;

      if (ids.length === 0) {
        resolve();
        return;
      }

      try {
        const rsp = await api.post<{ list: Staff[]; page: Page }>({
          url: `${StaffEndpoint}/selector`,
          data: ids,
        });
        rsp.list.forEach(s => staffViewCache.set(s.id, s));
        resolve();
      } catch (e) {
        reject(e);
      }
    }, 0);
  });

  return pendingBatchPromise;
}

class StaffDataSource extends DataSource<Staff, StaffFilterValue> {
  getKey(entity: Staff): ID {
    return entity.id;
  }
  getLabel(staff: Staff): React.ReactNode {
    return <>{staff.nickname}</>;
  }
  getValue(entity: Staff): ID {
    return entity.id;
  }
  getEntity(id: ID): Promise<Staff> {
    throw new Error('Method not implemented.');
  }
  async getList({ pageParam, filter }: { pageParam?: PageParam; filter?: StaffFilterValue } = {}) {
    const rsp = await api.post<{
      list: Staff[];
      page: Page;
    }>({
      url: `${StaffEndpoint}/selector`,
      data: [],
      params: { ...pageParam, ...filter },
    });
    this.pageInfo = rsp.page;
    return rsp.list;
  }
  async getViewList({ pageParam, filter, value }: { pageParam?: PageParam; filter?: StaffFilterValue; value: ID[] }) {
    // 分离已缓存和未缓存的 ID
    const uncachedIds: number[] = [];
    for (const id of value) {
      if (!staffViewCache.has(id as number)) {
        uncachedIds.push(id as number);
      }
    }

    // 将未缓存的 ID 加入批处理队列
    if (uncachedIds.length > 0) {
      uncachedIds.forEach(id => pendingStaffIds.add(id));
      await scheduleStaffBatchFlush();
    }

    // 从缓存返回结果
    return value.map(id => staffViewCache.get(id as number)!).filter(Boolean);
  }
  getPageInfo(): Page {
    return this.pageInfo;
  }
}

class OperatorDataSource extends StaffDataSource {
  async getList(args: { pageParam?: PageParam; filter?: StaffFilterValue }) {
    const list = await super.getList(args);
    return list.filter(v => v.isOperator);
  }
}

/**
 * 员工
 */
export const StaffSelector = createSelector({ datasource: new StaffDataSource() });
export const StaffModalViewer = createModalViewer({
  datasource: new StaffDataSource(),
  tableColumns: [
    { title: '员工姓名', dataIndex: 'nickname' },
    // { title: '账号', dataIndex: 'username' },
  ],
});
export const StaffModalViewerWithTrigger = ({
  ids,
  children,
  title,
}: {
  ids: number[];
  children: React.ReactChild;
  title: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      {open && (
        <StaffModalViewer
          title={title}
          open={open}
          value={ids}
          onCancel={() => {
            setOpen(false);
          }}
        />
      )}
      {React.cloneElement(children as React.ReactElement, { onClick: () => setOpen(true) })}
    </>
  );
};

/**
 * 美容师
 */
export const OperatorSelector = createSelector({ datasource: new OperatorDataSource() });

export const OperatorModalSelector = createModalSelector({
  datasource: new OperatorDataSource(),
  tableColumns: [
    { title: '员工名', dataIndex: 'nickname' },
    { title: '账号', dataIndex: 'username' },
  ],
  Filter: StaffFilter,
});

export const StaffFieldViewer = createFieldViewer({
  datasource: new StaffDataSource(),
});
